<template>
  <Sidebar
    v-model:visible="visible"
    class="p-sidebar-lg modern-sidebar"
    position="right"
    :showCloseIcon="false"
  >
    <Loading v-if="creating" :title="'Creating reward...'" :full="false" />
    <div v-show="!creating" class="reward-form-container">
      <div class="flex align-items-center mb-4">
        <h3 class="text-xl font-semibold m-0">✨ Create reward</h3>
      </div>
      <Form
        :fields="createFields"
        :context="newEntity"
        @onChange="handleFormChangeAndUpdateType"
        @onAction="onCreate"
        :actionLabel="'Create reward'"
        :actionIcon="'pi pi-plus-circle'"
        class="reward-form"
      />
    </div>
  </Sidebar>
</template>

<script>
import { computed } from "vue";
import { ref, watch, onMounted } from "vue";
import { RewardType } from "../utils/RewardUtils";
import { useStore } from "vuex";
import { useToast } from "primevue/usetoast";

export default {
  components: {},
  emits: ["update:isVisible", "onCreate"],
  props: ["isVisible", "creating"],
  setup(props, { emit }) {
    const visible = ref(false);
    const store = useStore();
    const toast = useToast();
    const venues = ref([]);

    const targetTypes = [
      { title: "All", id: "All" },
      { title: "Free only", id: "FreeOnly" },
      { title: "Subscription only", id: "SubscribersOnly" },
      { title: "Staff only", id: "Staff" },
    ];

    const updateEntity = (val) => {
      selectedRewardType.value = val?.rewardType;
    };

    const selectedRewardType = ref(RewardType.SpendReward);

    const currentLocalTime = computed(() => {
      const now = new Date();
      const hours = now.getHours().toString().padStart(2, '0');
      const minutes = now.getMinutes().toString().padStart(2, '0');
      return `${hours}:${minutes}`;
    });

    // Get the user's timezone
    const detectedTimeZone = computed(() => {
      return Intl.DateTimeFormat().resolvedOptions().timeZone;
    });

    const emptyTranslations = {
      translations: [
        { languageCode: "en", text: "" },
        { languageCode: "da", text: "" },
        { languageCode: "fi", text: "" },
        { languageCode: "no", text: "" },
      ],
    };
    const newEntity = ref({
      rewardType: RewardType.SpendReward,
      titleLongLocalized: { ...emptyTranslations },
      titleShortLocalized: { ...emptyTranslations },
      bottomTitleLocalized: { ...emptyTranslations },
      bottomIcon: "",
      descriptionLocalized: { ...emptyTranslations },
      rewardShortLocalized: { ...emptyTranslations },
      isRecurring: false,
      maxTotalCompletions: 0,
      dailyLimit: 0,
      weeklyLimit: 0,
      monthlyLimit: 0,
      expireDate: new Date().toJSON(),
      enabled: true,
      automaticClaim: true,
      rewardConfigurations: [],
      targetType: "All",
      requiresActivation: false,
      allowedVenueIds: [],
      amountTarget: 0,
      numberOfFriendsToInvite: 0,
      minNumberOfVisits: 0,
      canRevisitVenue: false,
      targetQuantity: 0,
      allowedProductTitles: [],
      targetNumberOfDays: 0,
      location: {
        street: "",
        postalCode: "",
        city: "",
        country: "",
        latitude: null,
        longitude: null,
      },
      activeTimeInterval: {
        activeFrom: null,
        activeTo: null,
        activeDays: [],
        activeDates: null,
        oncePerDayLimit: false,
        timeZoneId: detectedTimeZone.value,
      },
      pointsAwarded: null,
    });

    const rewardTypes = [
      { title: "Spending", id: RewardType.SpendReward },
      { title: "Invites", id: RewardType.InviteFriendReward },
      { title: "Product Purchase", id: RewardType.ProductPurchaseReward },
      { title: "Subscription Duration", id: RewardType.SubscriptionDurationReward },
      { title: "Venue Visit", id: RewardType.VenueVisitReward },
    ];

    async function loadVenues() {
      try {
        await store.dispatch("venues/ensureVenueListItems");
        const loadedVenues = store.getters["venues/venueListItems"];
        venues.value = loadedVenues;
      } catch (err) {
        toast.add({
          severity: "error",
          summary: "Error",
          detail: "Could not load Venues: " + err,
          life: 3000,
        });
      }
    }

    const baseFields = [
      {
        id: "rewardType",
        type: "dropdown",
        title: "🎯 Reward Type",
        help: "Specifies the reward type",
        config: {
          options: rewardTypes,
          optionLabel: "title",
          optionValue: "id",
          placeholder: "Please select type",
        },
      },
      {
        id: "titleLongLocalized",
        type: "localized-text",
        title: "📝 Long Title",
        help: "Full title of the reward",
      },
      {
        id: "titleShortLocalized",
        type: "localized-text",
        title: "✏️ Short Title",
        help: "Short title of the reward",
      },
      {
        id: "bottomTitleLocalized",
        type: "localized-text",
        title: "⬇️ Bottom Title",
        help: "Bottom section title",
      },
      {
        id: "bottomIcon",
        type: "imageUpload",
        title: "🖼️ Bottom Icon",
        help: "Icon for the bottom section",
      },
      {
        id: "descriptionLocalized",
        type: "localized-html",
        title: "📄 Description",
        config: {
          blackTextColor: true,
        },
        help: "Detailed description of the reward",
      },
      {
        id: "rewardShortLocalized",
        type: "localized-text",
        title: "💝 Short Reward",
        help: "Brief description of the reward",
      },
      {
        id: "isRecurring",
        type: "switch",
        title: "🔄 Recurring",
        help: "Indicates if the reward is recurring",
      },
      {
        id: "maxTotalCompletions",
        type: "number-input",
        title: "🎯 Max Total Completions",
        help: "Maximum total number of times this reward can be completed (0 = unlimited). If set, monthly limit must also be set.",
        config: {
          min: 0,
          placeholder: "0",
        },
        condition: (context) => context.isRecurring === true,
      },
      {
        id: "dailyLimit",
        type: "number-input",
        title: "📅 Daily Limit",
        help: "Maximum number of completions per day (0 = unlimited). Cannot be unlimited or exceed weekly limit if weekly is set.",
        config: {
          min: 0,
          placeholder: "0",
        },
        condition: (context) => context.isRecurring === true,
      },
      {
        id: "weeklyLimit",
        type: "number-input",
        title: "📆 Weekly Limit",
        help: "Maximum number of completions per week (0 = unlimited). Cannot be unlimited or exceed monthly limit if monthly is set.",
        config: {
          min: 0,
          placeholder: "0",
        },
        condition: (context) => context.isRecurring === true,
      },
      {
        id: "monthlyLimit",
        type: "number-input",
        title: "🗓️ Monthly Limit",
        help: "Maximum number of completions per month (0 = unlimited). Cannot be unlimited or exceed max total if max total is set.",
        config: {
          min: 0,
          placeholder: "0",
        },
        condition: (context) => context.isRecurring === true,
      },
      {
        id: "expireDate",
        type: "calendar",
        title: "📅 Expiration Date",
        help: "Date when the reward expires",
        config: {
          dateFormat: "dd-mm-yy",
        },
      },
      {
        id: "enabled",
        type: "switch",
        title: "🔌 Enabled",
        help: "Indicates if the reward is enabled",
      },
      // {
      //   id: "automaticClaim",
      //   type: "switch",
      //   title: "Automatic Claim",
      //   help: "Specifies if the reward is automatically claimed",
      // },
      {
        id: "targetType",
        type: "dropdown",
        title: "🎯 Target Type",
        help: "Specifies the target type for the reward",
        config: {
          options: targetTypes,
          optionLabel: "title",
          optionValue: "id",
          placeholder: "Please select type",
        },
      },
      {
        id: "requiresActivation",
        type: "switch",
        title: "🔑 Requires Activation",
        help: "Indicates if user must manually activate the reward.",
      },
      {
        id: "pointsAwarded",
        type: "number-input",
        title: "⭐ Points Awarded",
        help: "Points awarded when this reward is completed (optional)",
        config: {
          min: 0,
          placeholder: "0",
        },
      },

      {
        id: "rewardConfigurations",
        type: "perkConfig",
        title: "🎁 Rewards",
        config: {
          title: "🎁 Reward Perks",
          help: "Perks given when a user completes the reward.",
          enabled: true,
          configs: [],
        },
      },
    ];

    const amountTargetField = {
      id: "amountTarget",
      type: "number-input",
      title: "💰 Amount Target",
      help: "The amount user needs to spend to get the reward.",
    };

    const allowedVenueIdsField = {
      id: "allowedVenueIds",
      type: "multi",
      title: "🏢 Allowed Venues",
      help: "Select venues where this reward can be progressed.",
      config: {
        options: venues,
        optionLabel: "name",
        optionValue: "id",
        placeholder: "Select venues",
      },
    };

    const numberOfFriendsToInviteField = {
      id: "numberOfFriendsToInvite",
      type: "number-input",
      title: "👥 Number of Friends to Invite",
      help: "The number of friends user needs to invite.",
    };

    const minNumberOfVisitsField = {
      id: "minNumberOfVisits",
      type: "number-input",
      title: "🚶 Minimum Number of Visits",
      help: "The minimum number of venue visits required. Leave zero if user should visit all venues once.",
    };

    const canRevisitVenueField = {
      id: "canRevisitVenue",
      type: "switch",
      title: "🔄 Can Revisit Venue",
      help: "Allow users to revisit the same venue instead of requiring all venues to be visited. When enabled, minimum visits must be greater than 1.",
    };

    const targetQuantityField = {
      id: "targetQuantity",
      type: "number-input",
      title: "🎯 Number of products purchased",
      help: "The number of products user needs to purchase.",
    };

    const allowedProductTitlesField = {
      id: "allowedProductTitles",
      type: "chips",
      title: "🏷️ Included product Titles",
      help: "Type product title and press Enter to add to list.",
    };

    const targetNumberOfDaysField = {
      id: "targetNumberOfDays",
      type: "number-input",
      title: "📅 Number of days with subscription",
      help: "The number of days with subscription required.",
    };

    const locationField = {
      id: "location",
      type: "GoogleMapsAddress",
      title: "📍 Location",
      help: "Optional specific location for this reward",
    };

    const activeDaysField = {
      id: "activeTimeInterval.activeDays",
      type: "multi",
      title: "📅  Active Days",
      help: "Optional specific days when this reward is active",
      path: "activeTimeInterval.activeDays",
      config: {
        options: [
          { title: "Sunday", id: 0 },
          { title: "Monday", id: 1 },
          { title: "Tuesday", id: 2 },
          { title: "Wednesday", id: 3 },
          { title: "Thursday", id: 4 },
          { title: "Friday", id: 5 },
          { title: "Saturday", id: 6 },
        ],
        optionLabel: "title",
        optionValue: "id",
        placeholder: "Select active days",
      },
    };

    const activeDatesField = {
      id: "activeTimeInterval.activeDates",
      type: "calendar",
      title: "📅 Specific Active Dates",
      help: `Optional specific dates when this reward is active in ${detectedTimeZone.value}`,
      path: "activeTimeInterval.activeDates",
      config: {
        selectionMode: "multiple",
        dateFormat: "dd-mm-yy",
        placeholder: "Select specific dates",
        showButtonBar: true,
        showIcon: true
      },
    };

    const oncePerDayLimitField = {
      id: "oncePerDayLimit",
      type: "switch",
      title: "🔒 Once Per Day Limit",
      help: "If enabled, users can only progress once per day on this reward",
      path: "oncePerDayLimit",
    };

    const timeZoneField = {
      id: "activeTimeInterval.timeZoneId",
      type: "text-input",
      title: "🌍 Timezone",
      help: `Timezone for the active time interval (auto-detected)`,
      path: "activeTimeInterval.timeZoneId",
      config: {
        readonly: true,
      },
    };

    // Current form context for conditional field evaluation
    const currentFormContext = ref(newEntity.value);

    // All available fields before filtering
    const allFields = computed(() => {
      const dynamicAllowedVenueIdsField = {
        ...allowedVenueIdsField,
        config: {
          ...allowedVenueIdsField.config,
          options: venues.value,
        },
      };

      const activeFromField = {
        id: "activeTimeInterval.activeFrom",
        type: "time-only",
        title: "⏰ Active From (Time)",
        help: `Optional reward start time in ${detectedTimeZone.value} (Current time: ${currentLocalTime.value})`,
        path: "activeTimeInterval.activeFrom",
      };

      const activeToField = {
        id: "activeTimeInterval.activeTo", 
        type: "time-only",
        title: "⏰ Active To (Time)",
        help: `Optional reward end time in ${detectedTimeZone.value} (Current time: ${currentLocalTime.value})`,
        path: "activeTimeInterval.activeTo",
      };

      if (selectedRewardType.value === RewardType.SpendReward)
        return [...baseFields, amountTargetField, dynamicAllowedVenueIdsField, locationField, activeFromField, activeToField, activeDaysField, activeDatesField, timeZoneField];
      
      if (selectedRewardType.value === RewardType.InviteFriendReward)
        return [...baseFields, numberOfFriendsToInviteField, activeFromField, activeToField, activeDaysField, activeDatesField, timeZoneField];
      
      if (selectedRewardType.value === RewardType.VenueVisitReward)
        return [...baseFields, minNumberOfVisitsField, canRevisitVenueField, dynamicAllowedVenueIdsField, locationField, activeFromField, activeToField, activeDaysField, activeDatesField, oncePerDayLimitField, timeZoneField];
      
      if (selectedRewardType.value === RewardType.ProductPurchaseReward)
        return [...baseFields, targetQuantityField, allowedProductTitlesField, dynamicAllowedVenueIdsField, locationField, activeFromField, activeToField, activeDaysField, activeDatesField, timeZoneField];
      
      if (selectedRewardType.value === RewardType.SubscriptionDurationReward)
        return [...baseFields, targetNumberOfDaysField];

      return baseFields;
    });

    // Computed fields that filter based on current form context
    const createFields = computed(() => {
      return allFields.value.filter(field => {
        if (field.condition && typeof field.condition === 'function') {
          return field.condition(currentFormContext.value);
        }
        return true;
      });
    });

    async function onCreate(formContext) {
      // Validation for VenueVisitReward with CanRevisitVenue
      if (formContext.rewardType === RewardType.VenueVisitReward && formContext.canRevisitVenue) {
        if (formContext.minNumberOfVisits <= 1) {
          toast.add({
            severity: "error",
            summary: "Validation Error",
            detail: "When 'Can Revisit Venue' is enabled, minimum number of visits must be greater than 1.",
            life: 5000,
          });
          return;
        }
        
        if (!formContext.allowedVenueIds || formContext.allowedVenueIds.length === 0) {
          toast.add({
            severity: "error",
            summary: "Validation Error", 
            detail: "At least one venue must be selected for venue visit rewards.",
            life: 5000,
          });
          return;
        }
      }
      
      // Validation for recurring reward completion limits
      if (formContext.isRecurring) {
        const maxTotal = formContext.maxTotalCompletions || 0;
        const monthly = formContext.monthlyLimit || 0;
        const weekly = formContext.weeklyLimit || 0;
        const daily = formContext.dailyLimit || 0;
        
        // Monthly can't be higher than max total (unless max total is unlimited/0)
        if (maxTotal > 0 && monthly > 0 && monthly > maxTotal) {
          toast.add({
            severity: "error",
            summary: "Validation Error",
            detail: "Monthly limit cannot be higher than max total completions.",
            life: 5000,
          });
          return;
        }
        
        // If max total is set, monthly cannot be unlimited
        if (maxTotal > 0 && monthly === 0) {
          toast.add({
            severity: "error",
            summary: "Validation Error",
            detail: "Monthly limit cannot be unlimited when max total completions is set.",
            life: 5000,
          });
          return;
        }
        
        // Weekly can't be higher than monthly (unless monthly is unlimited/0)
        if (monthly > 0 && weekly > 0 && weekly > monthly) {
          toast.add({
            severity: "error",
            summary: "Validation Error",
            detail: "Weekly limit cannot be higher than monthly limit.",
            life: 5000,
          });
          return;
        }
        
        // If monthly is set, weekly cannot be unlimited
        if (monthly > 0 && weekly === 0) {
          toast.add({
            severity: "error",
            summary: "Validation Error",
            detail: "Weekly limit cannot be unlimited when monthly limit is set.",
            life: 5000,
          });
          return;
        }
        
        // Daily can't be higher than weekly (unless weekly is unlimited/0)
        if (weekly > 0 && daily > 0 && daily > weekly) {
          toast.add({
            severity: "error",
            summary: "Validation Error",
            detail: "Daily limit cannot be higher than weekly limit.",
            life: 5000,
          });
          return;
        }
        
        // If weekly is set, daily cannot be unlimited
        if (weekly > 0 && daily === 0) {
          toast.add({
            severity: "error",
            summary: "Validation Error",
            detail: "Daily limit cannot be unlimited when weekly limit is set.",
            life: 5000,
          });
          return;
        }
      }
      
      formContext.rewardConfigurations = formContext?.rewardConfigurations?.map(
        (x) => {
          if (x.venueId && !x.venueId?.id)
            return { ...x, venueId: { id: x.venueId } };
          return x;
        }
      );
      
      // Convert empty location object to null
      if (formContext.location && 
          (!formContext.location.street || formContext.location.street.trim() === "")) {
        console.log('Converting empty location to null');
        formContext.location = null;
      }

      // Ensure recurring fields are always included when recurring is enabled
      if (formContext.isRecurring) {
        if (formContext.maxTotalCompletions === undefined || formContext.maxTotalCompletions === null) {
          formContext.maxTotalCompletions = 0;
        }
        if (formContext.dailyLimit === undefined || formContext.dailyLimit === null) {
          formContext.dailyLimit = 0;
        }
        if (formContext.weeklyLimit === undefined || formContext.weeklyLimit === null) {
          formContext.weeklyLimit = 0;
        }
        if (formContext.monthlyLimit === undefined || formContext.monthlyLimit === null) {
          formContext.monthlyLimit = 0;
        }
      }
      
      // Handle activeTimeInterval fields
      if (formContext.activeTimeInterval) {
        // Convert Date objects and ISO strings to time strings (HH:mm format) for the API
        if (formContext.activeTimeInterval.activeFrom) {
          if (formContext.activeTimeInterval.activeFrom instanceof Date) {
            const hours = formContext.activeTimeInterval.activeFrom.getHours().toString().padStart(2, '0');
            const minutes = formContext.activeTimeInterval.activeFrom.getMinutes().toString().padStart(2, '0');
            formContext.activeTimeInterval.activeFrom = `${hours}:${minutes}`;
          } else if (typeof formContext.activeTimeInterval.activeFrom === 'string') {
            // Handle ISO datetime strings or time strings
            if (formContext.activeTimeInterval.activeFrom.includes('T') || formContext.activeTimeInterval.activeFrom.includes('Z')) {
              // It's an ISO datetime string, extract time part
              const date = new Date(formContext.activeTimeInterval.activeFrom);
              const hours = date.getHours().toString().padStart(2, '0');
              const minutes = date.getMinutes().toString().padStart(2, '0');
              formContext.activeTimeInterval.activeFrom = `${hours}:${minutes}`;
            }
            // If it's already in HH:mm format, leave it as is
          }
        }
        
        if (formContext.activeTimeInterval.activeTo) {
          if (formContext.activeTimeInterval.activeTo instanceof Date) {
            const hours = formContext.activeTimeInterval.activeTo.getHours().toString().padStart(2, '0');
            const minutes = formContext.activeTimeInterval.activeTo.getMinutes().toString().padStart(2, '0');
            formContext.activeTimeInterval.activeTo = `${hours}:${minutes}`;
          } else if (typeof formContext.activeTimeInterval.activeTo === 'string') {
            // Handle ISO datetime strings or time strings
            if (formContext.activeTimeInterval.activeTo.includes('T') || formContext.activeTimeInterval.activeTo.includes('Z')) {
              // It's an ISO datetime string, extract time part
              const date = new Date(formContext.activeTimeInterval.activeTo);
              const hours = date.getHours().toString().padStart(2, '0');
              const minutes = date.getMinutes().toString().padStart(2, '0');
              formContext.activeTimeInterval.activeTo = `${hours}:${minutes}`;
            }
            // If it's already in HH:mm format, leave it as is
          }
        }
        
        // Ensure timezone is set
        if (!formContext.activeTimeInterval.timeZoneId) {
          formContext.activeTimeInterval.timeZoneId = detectedTimeZone.value;
        }
        
        // Initialize empty arrays if needed
        if (!formContext.activeTimeInterval.activeDays) {
          formContext.activeTimeInterval.activeDays = [];
        }
        
        // Convert Date objects to ISO date strings for activeDates
        if (formContext.activeTimeInterval.activeDates && Array.isArray(formContext.activeTimeInterval.activeDates)) {
          formContext.activeTimeInterval.activeDates = formContext.activeTimeInterval.activeDates.map(date => {
            if (date instanceof Date) {
              // Use local date methods to avoid timezone conversion issues
              const year = date.getFullYear();
              const month = String(date.getMonth() + 1).padStart(2, '0');
              const day = String(date.getDate()).padStart(2, '0');
              return `${year}-${month}-${day}`;
            }
            return date;
          }).filter(date => date); // Remove any null/undefined values
        } else {
          // Convert null to empty array for API
          formContext.activeTimeInterval.activeDates = [];
        }
      } else {
        formContext.activeTimeInterval = {
          activeFrom: null,
          activeTo: null,
          activeDays: [],
          activeDates: [],
          oncePerDayLimit: false,
          timeZoneId: detectedTimeZone.value
        };
      }
      
      // Debug: Log what we're actually emitting
      console.log('CreateRewardSidebar - About to emit onCreate with:', {
        maxTotalCompletions: formContext.maxTotalCompletions,
        dailyLimit: formContext.dailyLimit,
        weeklyLimit: formContext.weeklyLimit,
        monthlyLimit: formContext.monthlyLimit,
        isRecurring: formContext.isRecurring
      });
      
      emit("onCreate", formContext);
    }

    // Watch for changes from parent
    watch(
      () => props.isVisible,
      (newVal) => {
        visible.value = newVal;
      }
    );

    // Emit event when visibility changes
    watch(visible, (newVal) => {
      emit("update:isVisible", newVal);
    });

    // Handle mutual exclusivity between activeDates and activeDays
    function handleFormChange(formContext) {
      if (formContext.activeTimeInterval) {
        const activeDates = formContext.activeTimeInterval.activeDates;
        const activeDays = formContext.activeTimeInterval.activeDays;
        
        // If activeDates was just set and activeDays exists, clear activeDays
        if (activeDates && activeDates.length > 0 && activeDays && activeDays.length > 0) {
          formContext.activeTimeInterval.activeDays = [];
        }
        
        // If activeDays was just set and activeDates exists, clear activeDates
        if (activeDays && activeDays.length > 0 && activeDates && activeDates.length > 0) {
          formContext.activeTimeInterval.activeDates = [];
        }
      }
    }

    // Combined handler for form changes
    function handleFormChangeAndUpdateType(formContext) {
      // Update our local context reference for conditional field evaluation
      currentFormContext.value = { ...formContext };
      
      // Always ensure recurring fields exist in the form context, even when not visible
      if (!('maxTotalCompletions' in formContext)) {
        formContext.maxTotalCompletions = 0;
      }
      if (!('dailyLimit' in formContext)) {
        formContext.dailyLimit = 0;
      }
      if (!('weeklyLimit' in formContext)) {
        formContext.weeklyLimit = 0;
      }
      if (!('monthlyLimit' in formContext)) {
        formContext.monthlyLimit = 0;
      }
      
      handleFormChange(formContext);
      updateEntity(formContext);
    }

    // Load venues on component mount
    onMounted(async () => {
      await loadVenues();
    });

    return {
      visible,
      newEntity,
      createFields,
      onCreate,
      updateEntity,
      handleFormChangeAndUpdateType,
    };
  },
};
</script>

<style scoped>

.modern-sidebar {
  --sidebar-width: 550px;
}

.modern-sidebar :deep(.p-sidebar) {
  border-radius: 16px 0 0 16px;
  box-shadow: -5px 0 30px rgba(0, 0, 0, 0.1);
}

.reward-form-container {
  padding: 0.5rem;
}

.reward-form :deep(.p-dropdown),
.reward-form :deep(.p-calendar),
.reward-form :deep(.p-inputtext),
.reward-form :deep(.p-multiselect) {
  border-radius: 8px;
}

.reward-form :deep(.p-button) {
  border-radius: 8px;
  transition: transform 0.2s;
}

.reward-form :deep(.p-button:hover) {
  transform: translateY(-1px);
}

.reward-form :deep(.p-field) {
  margin-bottom: 1.5rem;
}

.reward-form :deep(.p-field label) {
  margin-bottom: 0.5rem;
  font-weight: 500;
}

/* Add subtle hover effect to form elements */
.reward-form :deep(.p-inputtext:hover),
.reward-form :deep(.p-dropdown:hover),
.reward-form :deep(.p-multiselect:hover) {
  border-color: var(--primary-color);
}

/* Improve spacing between sections */
.reward-form :deep(.form-section) {
  margin-bottom: 2rem;
  padding: 1rem;
  background: var(--surface-card);
  border-radius: 12px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
}
</style>
